import React, { useState } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Text,
  Switch,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { createPost } from '../services/database';

const PostComposer = ({ userId, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [isMemberOnly, setIsMemberOnly] = useState(false);

  const handlePost = async () => {
    if (!content.trim()) {
      Alert.alert('Error', 'Please enter some content for your post');
      return;
    }

    if (!userId) {
      Alert.alert('Error', 'User ID is required');
      return;
    }

    try {
      setPosting(true);

      // Create post using Supabase
      const postData = {
        author_id: userId,
        content: content.trim(),
        is_member_only: isMemberOnly,
      };

      const newPost = await createPost(postData);

      // Convert to camelCase for frontend
      const formattedPost = {
        id: newPost.id,
        content: newPost.content,
        createdAt: newPost.created_at,
        isMemberOnly: newPost.is_member_only || false,
      };

      setContent('');
      setIsMemberOnly(false);
      
      if (onPostCreated) {
        onPostCreated(formattedPost);
      }

      Alert.alert('Success', 'Announcement posted successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      const errorMessage = error?.message || 'Failed to create post';
      Alert.alert('Error', errorMessage);
    } finally {
      setPosting(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name="announcement" size={24} color="#2E7D32" />
        <Text style={styles.headerText}>Create Announcement</Text>
      </View>
      
      <TextInput
        style={styles.input}
        placeholder="What's happening at your club?"
        value={content}
        onChangeText={setContent}
        multiline
        numberOfLines={4}
        maxLength={500}
        editable={!posting}
      />
      
      {/* Member-Only Toggle */}
      <TouchableOpacity 
        style={styles.memberOnlyContainer}
        onPress={() => setIsMemberOnly(!isMemberOnly)}
        activeOpacity={0.7}
      >
        <View style={styles.memberOnlyLeft}>
          <Icon 
            name={isMemberOnly ? 'lock' : 'lock-open'} 
            size={20} 
            color={isMemberOnly ? '#1976D2' : '#999'} 
          />
          <View style={styles.memberOnlyTextContainer}>
            <Text style={[styles.memberOnlyLabel, isMemberOnly && styles.memberOnlyLabelActive]}>
              Members only
            </Text>
            <Text style={styles.memberOnlyHint}>
              {isMemberOnly 
                ? 'Only club members can see this' 
                : 'Everyone can see this post'}
            </Text>
          </View>
        </View>
        <Switch
          value={isMemberOnly}
          onValueChange={setIsMemberOnly}
          trackColor={{ false: '#e0e0e0', true: '#90CAF9' }}
          thumbColor={isMemberOnly ? '#1976D2' : '#f4f3f4'}
        />
      </TouchableOpacity>

      <View style={styles.footer}>
        <Text style={styles.charCount}>
          {content.length}/500
        </Text>
        
        <TouchableOpacity
          style={[styles.button, posting && styles.buttonDisabled]}
          onPress={handlePost}
          disabled={posting || !content.trim()}
        >
          {posting ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <>
              <Icon name="send" size={18} color="white" />
              <Text style={styles.buttonText}>Post</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginVertical: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginLeft: 8,
  },
  input: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    textAlignVertical: 'top',
    minHeight: 100,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  charCount: {
    fontSize: 14,
    color: '#999',
  },
  button: {
    backgroundColor: '#2E7D32',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  memberOnlyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  memberOnlyLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  memberOnlyTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  memberOnlyLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  memberOnlyLabelActive: {
    color: '#1976D2',
  },
  memberOnlyHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
});

export default PostComposer;

